import React, { useState, useEffect } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Package, FolderOpen, Mail, ShoppingCart, Wrench, History, Send, CheckCircle2, XCircle, Cloud, Database, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { useCurrentAdmin } from '../../lib/useCurrentAdmin';
import CloudinaryUsageCard from '../../components/admin/CloudinaryUsageCard';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [stats, setStats] = useState({
    products: 0,
    projects: 0,
    services: 0,
    contacts: 0,
    orders: 0,
    version: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [products, projects, services, contacts, orders, version] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/products`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/projects`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/services`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/contacts`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/orders`, { headers }),
        axios.get(`${BACKEND_URL}/api/admin/revisions/current`, { headers }),
      ]);

      setStats({
        products: products.data.length,
        projects: projects.data.length,
        services: services.data.length,
        contacts: contacts.data.length,
        orders: orders.data.length,
        version: version.data?.version ?? 0,
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const statCards = [
    { title: 'Total Products', value: stats.products, icon: Package, color: '#00CED1' },
    { title: 'Total Projects', value: stats.projects, icon: FolderOpen, color: '#00CED1' },
    { title: 'Services', value: stats.services, icon: Wrench, color: '#00CED1' },
    { title: 'Contact Forms', value: stats.contacts, icon: Mail, color: '#FF0000' },
    { title: 'Orders', value: stats.orders, icon: ShoppingCart, color: '#00CED1' },
    { title: 'Content Version', value: `v${stats.version}`, icon: History, color: '#00CED1' },
  ];

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
          <h1 className="text-3xl font-bold" data-testid="admin-dashboard-heading">Dashboard</h1>
          <IntegrationHealthPills />
        </div>

        {loading ? (
          <div className="text-center py-20">
            <p className="text-gray-600">Loading statistics...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {statCards.map((stat) => {
              const Icon = stat.icon;
              return (
                <Card key={stat.title} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-600 mb-1">{stat.title}</p>
                        <p className="text-3xl font-bold" style={{ color: stat.color }}>
                          {stat.value}
                        </p>
                      </div>
                      <div 
                        className="p-3 rounded-full"
                        style={{ backgroundColor: `${stat.color}20` }}
                      >
                        <Icon className="h-8 w-8" style={{ color: stat.color }} />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Welcome to Admin Portal</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-600">
                Manage your Brainwave Architectural Projects website content from this dashboard. 
                Use the sidebar to navigate to different sections.
              </p>
              <ul className="mt-4 space-y-2 text-sm text-gray-700">
                <li>• <strong>Products:</strong> Add, edit, or delete furniture and decor items</li>
                <li>• <strong>Projects:</strong> Manage your architectural project gallery</li>
                <li>• <strong>Services:</strong> Manage the services shown on the public site</li>
                <li>• <strong>Contact Forms:</strong> View and manage customer inquiries</li>
                <li>• <strong>Orders:</strong> Track customer orders and transactions</li>
                <li>• <strong>Versions:</strong> Every content change creates a version. Restore to a previous snapshot anytime.</li>
              </ul>
            </CardContent>
          </Card>

          <EmailDiagnosticPanel />
          <CloudinaryUsageCard />
          <PreInvestOutcomeCard />
        </div>
      </div>
    </AdminLayout>
  );
};

// ---------------------------------------------------------------------------
// Email diagnostic — admin-only panel that fires a test email through the
// SAME pipeline as the contact form. Surfaces the SendGrid status code,
// response body, and a masked preview of which API key is loaded so admins
// can debug deliverability after a redeploy without poking fake forms.
// ---------------------------------------------------------------------------
const EmailDiagnosticPanel = () => {
  const { isAdmin } = useCurrentAdmin();
  const [recipient, setRecipient] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  if (!isAdmin) return null;

  const run = async () => {
    setBusy(true);
    setResult(null);
    try {
      const token = localStorage.getItem('admin_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/email-test`,
        recipient ? { recipient } : {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setResult(res.data);
      if (res.data?.ok) {
        toast.success(`SendGrid accepted (${res.data.status_code})`);
      } else {
        toast.error(`SendGrid call failed`);
      }
    } catch (err) {
      setResult({ ok: false, error: err.response?.data?.detail || err.message });
      toast.error('Diagnostic failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="mt-6" data-testid="admin-email-diagnostic">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Send className="h-5 w-5" style={{ color: '#00CED1' }} />
          Email diagnostic
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-gray-600">
          Sends a one-off test email through the <strong>same EmailService</strong> the contact form uses,
          and shows the raw SendGrid response. Useful after a redeploy or when contact-form emails stop arriving.
        </p>
        <div className="flex flex-wrap gap-2 items-end">
          <div className="flex-grow min-w-[14rem]">
            <Label htmlFor="email-test-recipient" className="text-xs">Recipient (optional)</Label>
            <Input
              id="email-test-recipient"
              type="email"
              placeholder="Defaults to RECIPIENT_EMAIL"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              data-testid="admin-email-test-recipient"
            />
          </div>
          <Button
            onClick={run}
            disabled={busy}
            className="text-white font-semibold"
            style={{ backgroundColor: '#00CED1' }}
            data-testid="admin-email-test-send"
          >
            <Send className="h-4 w-4 mr-2" />
            {busy ? 'Sending…' : 'Send test email'}
          </Button>
        </div>

        {result && (
          <div
            className="border rounded-md p-3 text-xs font-mono space-y-1"
            style={result.ok
              ? { borderColor: '#00CED1', backgroundColor: 'rgba(0,206,209,0.05)' }
              : { borderColor: '#dc2626', backgroundColor: 'rgba(220,38,38,0.05)' }}
            data-testid="admin-email-test-result"
          >
            <p className="flex items-center gap-2 font-semibold text-sm">
              {result.ok
                ? <><CheckCircle2 className="h-4 w-4 text-green-600" /> SendGrid accepted</>
                : <><XCircle className="h-4 w-4 text-red-600" /> Failed</>}
              {result.status_code && <span className="text-gray-500">— status {result.status_code}</span>}
            </p>
            {result.config && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 text-[11px]">
                <p><span className="text-gray-500">Sender:</span> {result.config.sender_email || '—'}</p>
                <p><span className="text-gray-500">Recipient:</span> {result.config.recipient_used || '—'}</p>
                <p><span className="text-gray-500">API key:</span> {result.config.api_key_present ? result.config.api_key_masked : <span className="text-red-600">MISSING</span>}</p>
                <p><span className="text-gray-500">Default RECIPIENT_EMAIL:</span> {result.config.recipient_email_default || '—'}</p>
              </div>
            )}
            {result.body && (
              <details className="mt-2">
                <summary className="cursor-pointer text-gray-600">Raw response body</summary>
                <pre className="mt-1 whitespace-pre-wrap break-all text-[10px] text-gray-700">{result.body}</pre>
              </details>
            )}
            {result.error && (
              <p className="mt-2 text-red-700">Error: {result.error}</p>
            )}
            {result.headers?.['X-Message-Id'] && (
              <p className="text-gray-500">Look up <code>{result.headers['X-Message-Id']}</code> in the SendGrid Activity feed for delivery details.</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;

// ---------------------------------------------------------------------------
// IntegrationHealthPills — passive at-a-glance row of health chips for the
// three external dependencies the admin cares about: SendGrid (Email),
// Cloudinary, and MongoDB. Single backend roundtrip via /api/admin/health.
// Click the Email pill to scroll to the full diagnostic panel below.
// ---------------------------------------------------------------------------
const PILL_PRESETS = {
  loading: { color: '#9ca3af', bg: 'rgba(156,163,175,0.1)' },
  ok:      { color: '#16a34a', bg: 'rgba(22,163,74,0.1)' },
  warn:    { color: '#dc2626', bg: 'rgba(220,38,38,0.1)' },
  error:   { color: '#dc2626', bg: 'rgba(220,38,38,0.1)' },
};

const HealthPill = ({ status, label, title, Icon, onClick, testid }) => {
  const p = PILL_PRESETS[status] || PILL_PRESETS.loading;
  const Tag = onClick ? 'button' : 'span';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={title}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border transition-opacity ${onClick ? 'hover:opacity-80 cursor-pointer' : ''}`}
      style={{ color: p.color, borderColor: p.color, backgroundColor: p.bg }}
      data-testid={testid}
      data-health-status={status}
    >
      <Icon className={`h-3.5 w-3.5 ${status === 'loading' ? 'animate-spin' : ''}`} />
      {label}
    </Tag>
  );
};

const IntegrationHealthPills = () => {
  const { isAdmin } = useCurrentAdmin();
  const [health, setHealth] = useState(null); // null = loading; {} = response
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!isAdmin) return undefined;
    const run = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const res = await axios.get(
          `${BACKEND_URL}/api/admin/health`,
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (cancelled) return;
        setHealth(res.data || {});
      } catch (err) {
        if (cancelled) return;
        setErrored(true);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [isAdmin]);

  if (!isAdmin) return null;

  const scrollToDiagnostic = () => {
    const el = document.querySelector('[data-testid="admin-email-diagnostic"]');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Resolve status per integration
  const statusFor = (key) => {
    if (errored) return 'error';
    if (!health) return 'loading';
    const entry = health[key];
    if (!entry) return 'error';
    return entry.ok ? 'ok' : 'warn';
  };

  const labelFor = (key, fallback) => {
    if (!health) return `Checking ${fallback}…`;
    const entry = health[key];
    if (!entry) return `${fallback} unavailable`;
    return entry.ok ? `${fallback} OK` : `Check ${fallback.toLowerCase()} config`;
  };

  const titleFor = (key, fallback) => {
    if (errored) return 'Health endpoint failed';
    if (!health) return `Checking ${fallback}…`;
    const entry = health[key];
    if (!entry) return `${fallback} status unavailable`;
    return entry.detail || (entry.ok ? `${fallback} reachable` : `${fallback} not configured`);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="admin-health-pills">
      <HealthPill
        status={statusFor('email')}
        label={labelFor('email', 'Email')}
        title={titleFor('email', 'Email')}
        Icon={statusFor('email') === 'ok' ? CheckCircle2 : (statusFor('email') === 'loading' ? Send : XCircle)}
        onClick={scrollToDiagnostic}
        testid="admin-email-health-pill"
      />
      <HealthPill
        status={statusFor('cloudinary')}
        label={labelFor('cloudinary', 'Cloudinary')}
        title={titleFor('cloudinary', 'Cloudinary')}
        Icon={Cloud}
        testid="admin-cloudinary-health-pill"
      />
      <HealthPill
        status={statusFor('mongodb')}
        label={labelFor('mongodb', 'MongoDB')}
        title={titleFor('mongodb', 'MongoDB')}
        Icon={Database}
        testid="admin-mongo-health-pill"
      />
    </div>
  );
};


// ---------------------------------------------------------------------------
// PreInvestOutcomeCard — compact 30-day win/lose/open chart for the dashboard.
// Links through to the full Pre-Invest tool. Quietly hides itself if the user
// has never generated any predictions.
// ---------------------------------------------------------------------------
const PreInvestOutcomeCard = () => {
  const [data, setData] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('admin_token');
        const headers = { Authorization: `Bearer ${token}` };
        const [h, s] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/admin/preinvest/history-chart?days=30`, { headers }),
          axios.get(`${BACKEND_URL}/api/admin/preinvest/stats`, { headers }),
        ]);
        setData(h.data || []);
        setStats(s.data);
      } catch (err) {
        // Quietly fail — dashboard should not break if preinvest isn't seeded
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return null;
  if (!stats || stats.total === 0) return null; // Hide if no picks yet

  return (
    <Card data-testid="dashboard-preinvest-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <TrendingUp className="w-4 h-4 text-teal-600" />
          Pre-Invest · 30-day outcomes
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 flex-wrap mb-3 text-sm">
          <div><span className="text-gray-500">Picks: </span><b>{stats.total}</b></div>
          <div><span className="text-gray-500">Active: </span><b className="text-teal-700">{stats.active}</b></div>
          <div><span className="text-gray-500">Wins: </span><b className="text-emerald-600">{stats.wins}</b></div>
          <div><span className="text-gray-500">Losses: </span><b className="text-rose-600">{stats.losses}</b></div>
          <div><span className="text-gray-500">Win-rate: </span><b className="text-teal-700">{stats.win_rate_pct}%</b></div>
        </div>
        {data.length > 0 ? (
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="date" stroke="#64748b" fontSize={10} tickFormatter={(d) => d.slice(5)} />
              <YAxis stroke="#64748b" fontSize={10} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 6 }}
                labelFormatter={(d) => `Batch ${d}`}
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="wins" stackId="a" fill="#10b981" name="Wins" />
              <Bar dataKey="losses" stackId="a" fill="#ef4444" name="Losses" />
              <Bar dataKey="open" stackId="a" fill="#cbd5e1" name="Open" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="text-sm text-slate-400 py-6 text-center">No batches yet.</div>
        )}
        <div className="text-xs text-gray-500 mt-2 flex items-center justify-between">
          <a href="/admin/preinvest" className="text-teal-600 hover:underline">Open Pre-Invest →</a>
          <a href="/predictions" target="_blank" rel="noopener noreferrer" className="text-slate-400 hover:text-slate-600">Public landing page ↗</a>
        </div>
      </CardContent>
    </Card>
  );
};
